import { useQuery } from '@tanstack/react-query';
import { Card } from '@safetyhub/ui';
import { formatToLocal } from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import { getLegalDocumentCompliance, legalDocumentsQueryKeys } from '../../lib/legalDocuments';
import {
  complianceRatePercent,
  describeComplianceBreakdown,
} from '../../lib/legalDocumentLabels';

/**
 * 사업장 이행률 요약 — 2단계 스토리 L20(US-0719 「업체 사업주로서, 실무는 안 하지만 우리 사업장의 이행률을 한 눈에
 * 본다」). 사용자 결정 ⓐ **같은 화면 + 권한만 다름**: 현장 선택 화면 위에 두고, 실무 버튼은 기존 화면이
 * `obligation.manage`·`obligation.exempt`로 **숨긴다**(그 규약은 `SiteObligationsPage`가 소유한다 — 여기서 새로
 * 갈지 않는다). 그래서 `obligation.view`만 가진 사람은 이 요약과 목록을 보고 아무 실무 버튼도 보지 않는다.
 *
 * 무엇을 세는지(이번 기간 · 통 다섯이 분모 · 제외·기간 없음은 분모 밖 · 수검업체 미등록 현장은 합계 밖)는 서버가
 * 정한다(`LegalDocumentsService.getComplianceSummary` — 의무 목록 행을 그대로 센다). 이 컴포넌트는 그 수를 그린다.
 *
 * 호출부가 `obligation.view`로 가린다 — 그 키가 없으면 서버가 403이라 패널을 띄우지 않는다.
 */
export default function CompliancePanel({ companyId }: { companyId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: legalDocumentsQueryKeys.compliance(companyId),
    queryFn: () => getLegalDocumentCompliance(companyId),
  });

  const sites = data?.sites ?? [];
  const totalRate = data ? complianceRatePercent(data.total) : null;

  return (
    <section aria-labelledby="legal-compliance-heading" className="mb-6">
      <h2 id="legal-compliance-heading" className="mb-2 text-[17px] font-extrabold text-ink-900">
        이번 기간 이행률
      </h2>
      {isLoading ? (
        <div className="flex justify-center py-6" role="status" aria-label="이행률 불러오는 중">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-3">
          <p className="text-sm font-medium text-rose-700">
            {apiErrorMessage(error) ?? '이행률을 불러오지 못했습니다.'}
          </p>
        </div>
      ) : !data || sites.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="px-4 py-5 text-sm text-ink-500">
          이행률을 셀 현장이 없습니다.
        </Card>
      ) : (
        <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-ink-200 px-4 py-4 sm:px-6">
            <p className="text-sm font-bold text-ink-900">전체</p>
            <p data-testid="compliance-total-rate" className="text-[22px] font-extrabold text-ink-900">
              {totalRate === null ? '대상 없음' : `${totalRate}%`}
            </p>
            <p className="text-xs font-medium text-ink-500">
              {`대상 ${data.total.target}건 중 이행 ${data.total.fulfilledOnTime + data.total.fulfilledLate}건`}
              {data.total.target > 0 && ` · ${describeComplianceBreakdown(data.total)}`}
            </p>
            <p className="ml-auto text-xs text-ink-400">
              {`${formatToLocal(new Date(data.measuredAt))} 기준`}
            </p>
          </div>
          <ul className="divide-y divide-ink-200" aria-label="현장별 이행률">
            {sites.map(({ site, tracked, counts }) => {
              const rate = complianceRatePercent(counts);
              return (
                <li key={site.siteId} className="flex items-center gap-3 px-4 py-3 sm:px-6">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink-900">{site.siteName}</p>
                    <p className="mt-0.5 truncate text-xs text-ink-500">
                      {!tracked
                        ? '수검업체가 등록되지 않아 이행률을 세지 않습니다'
                        : counts.target === 0
                          ? '이번 기간 대상 문서가 없습니다'
                          : `대상 ${counts.target}건 · ${describeComplianceBreakdown(counts)}`}
                      {(counts.excluded > 0 || counts.unscheduled > 0) &&
                        tracked &&
                        ` (제외 ${counts.excluded} · 기간 없음 ${counts.unscheduled}건은 대상 밖)`}
                    </p>
                  </div>
                  <p className="flex-none text-sm font-bold text-ink-900">
                    {!tracked || rate === null ? '—' : `${rate}%`}
                  </p>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </section>
  );
}
